/**
 * Thin wrapper around Meta's official `meta-capi-param-builder-clientjs`.
 *
 * What the SDK does beyond Meta Pixel JS:
 *   1. Parses `fbclid` from the URL and writes the canonical `_fbc` cookie
 *      (`fb.0.<ts>.<fbclid>.<appendix>`) — same format the server SDK uses,
 *      so client + server stay in sync.
 *   2. Mints a stable `_fbp` if absent.
 *   3. Collects backup click IDs from Facebook/Instagram in-app browsers
 *      (Extended Browser Properties) — fbclid is often missing from the
 *      URL in those environments.
 *   4. Resolves the client IP (incl. IPv6) via Cloudflare's `/cdn-cgi/trace`
 *      — served from the edge on every CF-fronted hostname, no Worker hit,
 *      same-origin, returns the same IP the Worker sees in cf-connecting-ip.
 *
 * We initialise once at app boot, then `fireCapi()` reads the cached
 * fbp/fbc/IP synchronously and includes them in the `/api/capi` payload.
 * The server-side SDK falls back to its own resolution if the client
 * didn't send anything.
 */

// The published package's `main` is the UMD bundle, so we import the
// default export and destructure at call sites.
import clientParamBuilder from "meta-capi-param-builder-clientjs";

/**
 * Cloudflare's trace endpoint. Served directly by the CF edge on every
 * CF-fronted hostname — same-origin in prod, no Worker compute, no third
 * party, and the IP it returns is byte-for-byte the same value our Worker
 * sees in `cf-connecting-ip` (so client + server agree).
 *
 * Override via `VITE_FB_CLIENT_IP_URL` if you want a custom endpoint
 * (must return either the plain `/cdn-cgi/trace` key=value format or a
 * naked IP string in the body). Set to "" to disable client-side IP
 * collection entirely (the Worker will still fill in from cf-connecting-ip).
 *
 * In dev (localhost) the relative URL 404s because there's no Cloudflare
 * in front — that's fine; the server fills in IP from the request and
 * we don't ship real CAPI events from localhost anyway.
 */
const IP_TRACE_URL = (() => {
  const raw = import.meta.env.VITE_FB_CLIENT_IP_URL;
  if (raw === undefined) return "/cdn-cgi/trace";
  return String(raw).trim();
})();

let initPromise: Promise<void> | null = null;

/**
 * Fetch the IP from Cloudflare's trace endpoint (or a custom URL).
 * Parses `ip=…` from a `/cdn-cgi/trace`-style key=value body, with a
 * fallback to treating the body as a naked IP string. Returns "" on
 * any failure so the SDK falls through to server-side resolution.
 */
async function fetchClientIp(): Promise<string> {
  if (!IP_TRACE_URL) return "";
  try {
    const res = await fetch(IP_TRACE_URL, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return "";
    const text = (await res.text()).trim();
    // /cdn-cgi/trace returns lines like `ip=2603:…\nts=…\nuag=…`
    const match = /^ip=(.+)$/m.exec(text);
    if (match && match[1]) return match[1].trim();
    // Some endpoints (e.g. ipify) return a bare IP — accept that too,
    // but only if the body looks vaguely IP-shaped to avoid bringing
    // HTML error pages into our payload.
    if (/^[0-9a-fA-F:.]+$/.test(text)) return text;
    return "";
  } catch {
    return "";
  }
}

/**
 * Kick the client-side param builder once. Safe to call multiple times —
 * subsequent calls return the cached promise. Resolves when the SDK has
 * finished its URL/cookie/IP collection.
 */
export function initCapiClient(): Promise<void> {
  if (initPromise) return initPromise;
  if (typeof window === "undefined") return Promise.resolve();

  const getIpFn = IP_TRACE_URL ? fetchClientIp : undefined;

  initPromise = (async () => {
    try {
      // Pass null url so the SDK uses window.location internally.
      await clientParamBuilder.processAndCollectAllParams(null, getIpFn);
    } catch (err) {
      // Marketing failures must never break the funnel.
      console.warn("[capiClient] init failed", err);
    }
  })();

  return initPromise;
}

/**
 * Synchronous getters — safe to call after `initCapiClient()` has
 * resolved. Before that they may return null/empty, which is fine: the
 * server SDK fills in from the request's Cookie header.
 */
export function readCapiClientParams(): {
  fbp?: string;
  fbc?: string;
  clientIpAddress?: string;
} {
  if (typeof window === "undefined") return {};
  try {
    const fbp = clientParamBuilder.getFbp() || undefined;
    const fbc = clientParamBuilder.getFbc() || undefined;
    const clientIpAddress =
      clientParamBuilder.getClientIpAddress() || undefined;
    return { fbp, fbc, clientIpAddress };
  } catch {
    return {};
  }
}
